// BL-40 — a staff member's totals. ADMIN AND SUPERADMIN ONLY: the modal does not
// render this for anyone else, so neither query below ever runs for a hub
// manager (and `payroll_lines` would refuse one anyway — see useStaffEarnings).

import { bookingGroupStatus, byRowId, groupRowsByBooking } from "@bednbody/logic";
import { formatPHP } from "@bednbody/ui/lib/format";
import { CalendarCheckIcon, ReceiptIcon, WalletIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { StatTile } from "@/components/StatTile";
import type { RosterPerson } from "../components/types";
import { hasBookingHistory } from "./staff-detail.access";
import { useStaffBookings, useStaffEarnings } from "./staff-detail.hooks";

const dateLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-PH", { month: "short", day: "numeric" });

const LOADING = "…";

export function StaffTotals({ person }: { person: RosterPerson }) {
  const withBookings = hasBookingHistory(person.role);
  const { data: bookings } = useStaffBookings(withBookings ? person : null);
  const earnings = useStaffEarnings(person.id, withBookings);

  // Counted per booking, not per client row: a rider carries every client of a
  // group booking, and two rows of one booking are one job.
  const counts = useMemo(() => {
    const groups = groupRowsByBooking(bookings ?? [], (row) => row.booking_group_id, byRowId);
    const statuses = groups.map((group) => bookingGroupStatus(group.map((row) => row.status)));
    return {
      total: groups.length,
      completed: statuses.filter((status) => status === "completed").length,
      cancelled: statuses.filter((status) => status === "cancelled").length,
    };
  }, [bookings]);

  if (!withBookings) return null;

  const latest = earnings.data?.latest ?? null;

  return (
    <section aria-label="Totals" className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <StatTile
        detail={bookings ? `${counts.total} total · ${counts.cancelled} cancelled` : LOADING}
        icon={CalendarCheckIcon}
        label="Completed bookings"
        value={bookings ? counts.completed : LOADING}
      />
      <StatTile
        // Posted by payroll, so tonight's unsettled Session is not in it yet.
        detail={earnings.data ? `${formatPHP(earnings.data.paid)} paid out` : LOADING}
        highlight
        icon={WalletIcon}
        label="Posted earnings"
        value={
          earnings.isError
            ? "Unavailable"
            : earnings.data
              ? formatPHP(earnings.data.total)
              : LOADING
        }
      />
      <StatTile
        detail={
          latest
            ? `${dateLabel(latest.periodStart)} – ${dateLabel(latest.periodEnd)} · ${latest.status}`
            : earnings.data
              ? "No payroll run yet"
              : LOADING
        }
        icon={ReceiptIcon}
        label="Latest payroll"
        value={latest ? formatPHP(latest.total) : earnings.data ? "—" : LOADING}
      />
    </section>
  );
}
